// SNIGJ - Enhanced Citizen Portal Page v2
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import PrintableDocument from '../components/documents/PrintableDocument';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, FileText, CreditCard, Bell, AlertTriangle, CheckCircle, Clock,
  Calendar, MapPin, DollarSign, Shield, Scale, Smartphone, Building2,
  Wallet, Receipt, Download, Eye, Printer, MessageSquare, AlertCircle,
  Send, Users, Briefcase, PlaneTakeoff, History, Plus, Edit, Trash2,
  Phone, Mail, Home, Link2, X, Upload, File, Image, Video, Mic
} from 'lucide-react';
import { t, AVAILABLE_LANGUAGES } from '../lib/i18n';
import { toast } from 'sonner';
import api from '../lib/api';

const CitizenPortalPage = () => {
  const { user, language } = useAuth();
  const { displayAmount, preferredCurrency } = useCurrency();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingFee, setPayingFee] = useState(null);
  const [selectedFee, setSelectedFee] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showLawyerModal, setShowLawyerModal] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  
  // Extended data
  const [detentionHistory, setDetentionHistory] = useState(null);
  const [travelBanStatus, setTravelBanStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [convocationHistory, setConvocationHistory] = useState(null);
  const [messages, setMessages] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [family, setFamily] = useState([]);
  
  // Forms
  const [complaint, setComplaint] = useState({
    complaint_type: '',
    title: '',
    description: '',
    incident_date: '',
    incident_location: '',
    accused_info: '',
    attachments: []
  });
  const [complaintFiles, setComplaintFiles] = useState([]);
  const [newMessage, setNewMessage] = useState({ case_id: '', subject: '', content: '' });
  const [newLawyer, setNewLawyer] = useState({ name: '', bar_number: '', phone: '', email: '', address: '' });
  const [newFamilyMember, setNewFamilyMember] = useState({ 
    relationship: '', name: '', date_of_birth: '', national_id: '', phone: '', email: '' 
  });
  
  const printRef = useRef();

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [profileRes, detentionRes, travelRes, paymentsRes, convocationsRes, complaintsRes, lawyersRes, familyRes, messagesRes] = await Promise.all([
        api.get('/citizen/profile'),
        api.get('/citizen/detention-history').catch(() => ({ data: { detentions: [], detention_count: 0 } })),
        api.get('/citizen/travel-ban-status').catch(() => ({ data: { has_travel_ban: false } })),
        api.get('/citizen/payment-history').catch(() => ({ data: { payments: [], summary: {} } })),
        api.get('/citizen/convocation-history').catch(() => ({ data: { convocations: [] } })),
        api.get('/citizen/complaints').catch(() => ({ data: { complaints: [] } })),
        api.get('/citizen/lawyers').catch(() => ({ data: { lawyers: [] } })),
        api.get('/citizen/family').catch(() => ({ data: { family_members: [] } })),
        api.get('/citizen/messages').catch(() => ({ data: { conversations: [] } }))
      ]);
      
      setProfile(profileRes.data);
      setDetentionHistory(detentionRes.data);
      setTravelBanStatus(travelRes.data);
      setPaymentHistory(paymentsRes.data);
      setConvocationHistory(convocationsRes.data);
      setComplaints(complaintsRes.data.complaints || []);
      setLawyers(lawyersRes.data.lawyers || []);
      setFamily(familyRes.data.family_members || []);
      setMessages(messagesRes.data.conversations || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFee = async (feeId) => {
    setPayingFee(feeId);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payments/checkout', { 
        fee_id: feeId,
        origin_url: originUrl
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else if (response.data.redirect_url && response.data.form_data) {
        // MaishaPay POST redirect
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.redirect_url;
        Object.entries(response.data.form_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else if (response.data.test_mode) {
        // Test mode - payment auto-completed
        toast.success(response.data.message || (language === 'fr' ? 'Paiement test réussi' : 'Test payment successful'));
        setPayingFee(null);
        // Reload data to show updated fee status
        loadAllData();
      } else {
        toast.error(language === 'fr' ? 'Aucune passerelle de paiement disponible' : 'No payment gateway available');
        setPayingFee(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de paiement' : 'Payment error'));
      setPayingFee(null);
    }
  };

  const handleFileComplaint = async () => {
    try {
      // Upload files first if any
      let uploadedAttachments = [];
      if (complaintFiles.length > 0) {
        const formData = new FormData();
        complaintFiles.forEach(file => {
          formData.append('files', file);
        });
        try {
          const uploadRes = await api.post('/upload/complaint-attachments', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedAttachments = uploadRes.data.files || [];
        } catch (uploadError) {
          // Continue without attachments if upload fails
          console.error('File upload failed:', uploadError);
          toast.warning(language === 'fr' ? 'Fichiers non téléchargés, plainte soumise sans pièces jointes' : 'Files not uploaded, complaint submitted without attachments');
        }
      }

      const complaintData = {
        ...complaint,
        attachments: uploadedAttachments
      };

      const response = await api.post('/citizen/complaints', complaintData);
      toast.success(language === 'fr' 
        ? `Plainte ${response.data.complaint_id} enregistrée. Frais: ${response.data.fee_amount} USD` 
        : `Complaint ${response.data.complaint_id} filed. Fee: ${response.data.fee_amount} USD`);
      setShowComplaintModal(false);
      setComplaint({ complaint_type: '', title: '', description: '', incident_date: '', incident_location: '', accused_info: '', attachments: [] });
      setComplaintFiles([]);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleComplaintFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 50 * 1024 * 1024; // 50MB max per file
    const validFiles = files.filter(file => file.size <= maxSize);
    
    if (validFiles.length !== files.length) {
      toast.warning(language === 'fr' ? 'Certains fichiers dépassent 50MB et ont été ignorés' : 'Some files exceed 50MB and were ignored');
    }
    
    setComplaintFiles(prev => [...prev, ...validFiles]);
  };

  const removeComplaintFile = (index) => {
    setComplaintFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    const type = file.type;
    if (type.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
    if (type.startsWith('video/')) return <Video className="w-4 h-4 text-purple-500" />;
    if (type.startsWith('audio/')) return <Mic className="w-4 h-4 text-green-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const handleSendMessage = async () => {
    try {
      await api.post('/citizen/messages', newMessage);
      toast.success(language === 'fr' ? 'Message envoyé' : 'Message sent');
      setShowMessageModal(false);
      setNewMessage({ case_id: '', subject: '', content: '' });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleAddLawyer = async () => {
    try {
      await api.post('/citizen/lawyers', newLawyer);
      toast.success(language === 'fr' ? 'Avocat ajouté' : 'Lawyer added');
      setShowLawyerModal(false);
      setNewLawyer({ name: '', bar_number: '', phone: '', email: '', address: '' });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteLawyer = async (lawyerId) => {
    try {
      await api.delete(`/citizen/lawyers/${lawyerId}`);
      toast.success(language === 'fr' ? 'Avocat supprimé' : 'Lawyer removed');
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleAddFamily = async () => {
    try {
      const response = await api.post('/citizen/family', newFamilyMember);
      if (response.data.is_linked) {
        toast.success(language === 'fr' 
          ? `Membre ajouté et lié au compte existant de ${response.data.linked_user?.name}` 
          : `Member added and linked to existing account of ${response.data.linked_user?.name}`);
      } else {
        toast.success(language === 'fr' ? 'Membre de famille ajouté' : 'Family member added');
      }
      setShowFamilyModal(false);
      setNewFamilyMember({ relationship: '', name: '', date_of_birth: '', national_id: '', phone: '', email: '' });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.put(`/citizen/notifications/${notificationId}/read`);
      loadAllData();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handlePrintDocument = (item) => {
    setSelectedFee(item);
    setShowInvoiceModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      PENDING: 'bg-orange-100 text-orange-800',
      CLOSED: 'bg-gray-100 text-gray-800',
      ACTIVE: 'bg-green-100 text-green-800',
      PAID: 'bg-green-100 text-green-800',
      OVERDUE: 'bg-red-100 text-red-800',
      SUBMITTED: 'bg-blue-100 text-blue-800',
      DETAINED: 'bg-red-100 text-red-800',
      FREE: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'SUMMONS': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'PAYMENT_DUE': return <DollarSign className="w-5 h-5 text-yellow-600" />;
      case 'INVITATION': return <Calendar className="w-5 h-5 text-blue-600" />;
      case 'CASE_UPDATE': return <FileText className="w-5 h-5 text-purple-600" />;
      default: return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="h-8 w-64 skeleton" />
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 skeleton" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="citizen-portal">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">
                {language === 'fr' ? 'Mon Profil Justice' : 'My Justice Profile'}
              </h1>
              <p className="text-muted-foreground">
                {profile?.user?.name} • {profile?.user?.national_id || (language === 'fr' ? 'ID non renseigné' : 'ID not provided')}
              </p>
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setShowComplaintModal(true)} data-testid="file-complaint-btn">
              <AlertCircle className="w-4 h-4" />
              {language === 'fr' ? 'Déposer plainte' : 'File Complaint'}
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => setShowMessageModal(true)} data-testid="send-message-btn">
              <MessageSquare className="w-4 h-4" />
              {language === 'fr' ? 'Messagerie' : 'Messages'}
            </Button>
          </div>
        </div>

        {/* Travel Ban Alert */}
        {travelBanStatus?.has_travel_ban && (
          <Card className="border-red-500 bg-red-50 dark:bg-red-900/20" data-testid="travel-ban-alert">
            <CardContent className="p-4 flex items-center gap-4">
              <PlaneTakeoff className="w-8 h-8 text-red-600" />
              <div className="flex-1">
                <p className="font-bold text-red-700 dark:text-red-400">
                  {language === 'fr' ? '⚠️ INTERDICTION DE VOYAGE ACTIVE' : '⚠️ ACTIVE TRAVEL BAN'}
                </p>
                <p className="text-sm text-red-600 dark:text-red-500">
                  {travelBanStatus.ban_details?.reason || (language === 'fr' ? 'Contactez votre avocat ou le tribunal.' : 'Contact your lawyer or the court.')}
                </p>
              </div>
              <Badge variant="destructive">
                {travelBanStatus.ban_details?.severity || 'HIGH'}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{profile?.summary?.total_cases || 0}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Dossiers' : 'Cases'}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{detentionHistory?.detention_count || 0}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Arrestations' : 'Arrests'}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={profile?.summary?.pending_fees > 0 ? 'border-yellow-400' : ''}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{displayAmount(profile?.summary?.pending_fees_amount || 0)}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Frais en attente' : 'Pending Fees'}</p>
              </div>
            </CardContent>
          </Card>

          <Card className={profile?.summary?.unread_notifications > 0 ? 'border-red-400' : ''}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Bell className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{profile?.summary?.unread_notifications || 0}</p>
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Notifications' : 'Notifications'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Casier Judiciaire Status */}
        <Card className={profile?.judicial_record?.length === 0 ? 'border-green-400 bg-green-50 dark:bg-green-900/10' : 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'}>
          <CardContent className="p-4 flex items-center gap-4">
            {profile?.judicial_record?.length === 0 ? (
              <>
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-bold text-green-700 dark:text-green-400">
                    {language === 'fr' ? 'Casier Judiciaire Vierge' : 'Clean Criminal Record'}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-500">
                    {language === 'fr' ? 'Aucune condamnation enregistrée' : 'No convictions on record'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="w-8 h-8 text-yellow-600" />
                <div>
                  <p className="font-bold text-yellow-700 dark:text-yellow-400">
                    {language === 'fr' ? 'Casier Judiciaire' : 'Criminal Record'}
                  </p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-500">
                    {profile?.judicial_record?.length} {language === 'fr' ? 'entrée(s)' : 'entry(ies)'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="notifications" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="w-4 h-4" />
              {language === 'fr' ? 'Convocations' : 'Summons'}
              {profile?.summary?.unread_notifications > 0 && <Badge variant="destructive">{profile?.summary?.unread_notifications}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="cases" className="gap-2">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Dossiers' : 'Cases'}
            </TabsTrigger>
            <TabsTrigger value="fees" className="gap-2">
              <CreditCard className="w-4 h-4" />
              {language === 'fr' ? 'Frais' : 'Fees'}
            </TabsTrigger>
            <TabsTrigger value="complaints" className="gap-2">
              <AlertCircle className="w-4 h-4" />
              {language === 'fr' ? 'Plaintes' : 'Complaints'}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              {language === 'fr' ? 'Historique' : 'History'}
            </TabsTrigger>
            <TabsTrigger value="lawyers" className="gap-2">
              <Briefcase className="w-4 h-4" />
              {language === 'fr' ? 'Avocats' : 'Lawyers'}
            </TabsTrigger>
            <TabsTrigger value="family" className="gap-2">
              <Users className="w-4 h-4" />
              {language === 'fr' ? 'Famille' : 'Family'}
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Convocations & Notifications' : 'Summons & Notifications'}</CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.notifications?.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucune notification' : 'No notifications'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profile?.notifications?.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className={`p-4 rounded border cursor-pointer transition-colors ${!notif.is_read ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200' : 'hover:bg-muted/50'}`}
                        onClick={() => !notif.is_read && markNotificationRead(notif.notification_id)}
                        data-testid={`notification-${notif.notification_id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            notif.notification_type === 'SUMMONS' ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {getNotificationIcon(notif.notification_type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{notif.title}</h4>
                              {notif.is_urgent && <Badge variant="destructive">URGENT</Badge>}
                              {!notif.is_read && <Badge variant="secondary">{language === 'fr' ? 'Non lu' : 'Unread'}</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{notif.message}</p>
                            {notif.scheduled_date && (
                              <p className="text-sm flex items-center gap-1 text-primary">
                                <Calendar className="w-4 h-4" />
                                {new Date(notif.scheduled_date).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                              </p>
                            )}
                            {notif.location && (
                              <p className="text-sm flex items-center gap-1 mt-1">
                                <MapPin className="w-4 h-4" />
                                {notif.location}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(notif.created_at).toLocaleDateString()}
                            </span>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handlePrintDocument(notif); }}>
                              <Printer className="w-3 h-3 mr-1" />
                              {language === 'fr' ? 'Imprimer' : 'Print'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Mes Dossiers Judiciaires' : 'My Judicial Cases'}</CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.cases?.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun dossier' : 'No cases'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profile?.cases?.map((caseItem) => (
                      <div key={caseItem.case_id} className="p-4 rounded border hover:bg-muted/50">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm">{caseItem.case_number}</span>
                              <Badge className={getStatusBadge(caseItem.status)}>{caseItem.status}</Badge>
                            </div>
                            <h4 className="font-medium">{caseItem.title}</h4>
                            <p className="text-sm text-muted-foreground">{caseItem.description}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => { setNewMessage({ ...newMessage, case_id: caseItem.case_id }); setShowMessageModal(true); }}>
                              <MessageSquare className="w-3 h-3 mr-1" />
                              {language === 'fr' ? 'Message' : 'Message'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fees Tab */}
          <TabsContent value="fees">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {language === 'fr' ? 'Frais de Justice & Amendes' : 'Court Fees & Fines'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.fees?.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun frais' : 'No fees'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profile?.fees?.map((fee) => (
                      <div 
                        key={fee.fee_id} 
                        className={`p-4 rounded border ${fee.status === 'OVERDUE' ? 'border-red-400 bg-red-50 dark:bg-red-900/10' : fee.status === 'PAID' ? 'bg-green-50 dark:bg-green-900/10' : ''}`}
                        data-testid={`fee-${fee.fee_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getStatusBadge(fee.status)}>
                                {fee.status === 'PENDING' ? (language === 'fr' ? 'En attente' : 'Pending') :
                                 fee.status === 'PAID' ? (language === 'fr' ? 'Payé' : 'Paid') :
                                 fee.status === 'OVERDUE' ? (language === 'fr' ? 'En retard' : 'Overdue') : fee.status}
                              </Badge>
                              <span className="text-sm text-muted-foreground">{fee.fee_type?.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="font-medium">{fee.description}</p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              {language === 'fr' ? 'Échéance:' : 'Due:'} {fee.due_date}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold">{displayAmount(fee.amount, fee.currency || 'USD')}</p>
                            <p className="text-xs text-muted-foreground">
                              {fee.currency !== preferredCurrency && `(${fee.amount?.toFixed(2)} ${fee.currency || 'USD'})`}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Button variant="outline" size="sm" onClick={() => handlePrintDocument(fee)}>
                                <Printer className="w-3 h-3 mr-1" />
                                {language === 'fr' ? 'Imprimer' : 'Print'}
                              </Button>
                              {(fee.status === 'PENDING' || fee.status === 'OVERDUE') && (
                                <Button 
                                  size="sm"
                                  onClick={() => { setSelectedFee(fee); setShowPaymentModal(true); }}
                                  disabled={payingFee === fee.fee_id}
                                  data-testid={`pay-fee-${fee.fee_id}`}
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  {language === 'fr' ? 'Payer' : 'Pay'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Complaints Tab */}
          <TabsContent value="complaints">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{language === 'fr' ? 'Mes Plaintes' : 'My Complaints'}</CardTitle>
                <Button onClick={() => setShowComplaintModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  {language === 'fr' ? 'Nouvelle plainte' : 'New Complaint'}
                </Button>
              </CardHeader>
              <CardContent>
                {complaints.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucune plainte déposée' : 'No complaints filed'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {complaints.map((c) => (
                      <div key={c.complaint_id} className="p-4 rounded border">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm">{c.complaint_id}</span>
                              <Badge className={getStatusBadge(c.status)}>{c.status}</Badge>
                              <Badge variant="outline">{c.complaint_type?.replace(/_/g, ' ')}</Badge>
                            </div>
                            <h4 className="font-medium">{c.title}</h4>
                            <p className="text-sm text-muted-foreground">{c.description}</p>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(c.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Detention History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    {language === 'fr' ? 'Historique des détentions' : 'Detention History'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!detentionHistory?.detentions?.length ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                      {language === 'fr' ? 'Aucune détention enregistrée' : 'No detentions recorded'}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {detentionHistory.detentions.map((d, i) => (
                        <div key={i} className="p-3 rounded border">
                          <div className="flex items-center justify-between mb-1">
                            <Badge className={getStatusBadge(d.status)}>{d.type}</Badge>
                            <span className="text-xs">{d.date}</span>
                          </div>
                          <p className="text-sm">{d.reason}</p>
                          {d.release_reason && (
                            <p className="text-xs text-green-600 mt-1">
                              {language === 'fr' ? 'Libéré:' : 'Released:'} {d.release_reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payment History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    {language === 'fr' ? 'Historique des paiements' : 'Payment History'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <p className="text-lg font-bold text-green-600">{paymentHistory?.summary?.total_paid?.toFixed(2) || 0}</p>
                      <p className="text-xs">{language === 'fr' ? 'Payé' : 'Paid'}</p>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      <p className="text-lg font-bold text-yellow-600">{paymentHistory?.summary?.total_pending?.toFixed(2) || 0}</p>
                      <p className="text-xs">{language === 'fr' ? 'En attente' : 'Pending'}</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <p className="text-lg font-bold text-red-600">{paymentHistory?.summary?.total_overdue?.toFixed(2) || 0}</p>
                      <p className="text-xs">{language === 'fr' ? 'En retard' : 'Overdue'}</p>
                    </div>
                  </div>
                  {paymentHistory?.payments?.slice(0, 5).map((p, i) => (
                    <div key={i} className="flex justify-between items-center p-2 border-b last:border-0">
                      <span className="text-sm">{p.description || p.fee_type}</span>
                      <span className="font-bold text-green-600">{p.amount} USD</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Convocation History */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {language === 'fr' ? 'Historique des convocations' : 'Summons History'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!convocationHistory?.convocations?.length ? (
                    <p className="text-center py-6 text-muted-foreground">
                      {language === 'fr' ? 'Aucune convocation' : 'No summons'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {convocationHistory.convocations.slice(0, 6).map((c, i) => (
                        <div key={i} className="p-3 rounded border">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{c.notification_type}</Badge>
                            <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="font-medium text-sm">{c.title}</p>
                          {c.scheduled_date && (
                            <p className="text-xs text-primary mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {new Date(c.scheduled_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Lawyers Tab */}
          <TabsContent value="lawyers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{language === 'fr' ? 'Mes Avocats' : 'My Lawyers'}</CardTitle>
                <Button onClick={() => setShowLawyerModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  {language === 'fr' ? 'Ajouter avocat' : 'Add Lawyer'}
                </Button>
              </CardHeader>
              <CardContent>
                {lawyers.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun avocat enregistré' : 'No lawyers registered'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {lawyers.map((lawyer) => (
                      <div key={lawyer.lawyer_id} className="p-4 rounded border">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-bold">{lawyer.name}</h4>
                            <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Barreau:' : 'Bar:'} {lawyer.bar_number}</p>
                            <div className="mt-2 space-y-1 text-sm">
                              {lawyer.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{lawyer.phone}</p>}
                              {lawyer.email && <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{lawyer.email}</p>}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteLawyer(lawyer.lawyer_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Family Tab */}
          <TabsContent value="family">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{language === 'fr' ? 'Membres de la Famille' : 'Family Members'}</CardTitle>
                <Button onClick={() => setShowFamilyModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  {language === 'fr' ? 'Ajouter membre' : 'Add Member'}
                </Button>
              </CardHeader>
              <CardContent>
                {family.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{language === 'fr' ? 'Aucun membre enregistré' : 'No members registered'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {family.map((member) => (
                      <div key={member.member_id} className="p-4 rounded border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold">{member.name}</h4>
                            <Badge variant="outline" className="text-xs">{member.relationship}</Badge>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1 text-sm">
                          {member.national_id && <p className="text-muted-foreground">ID: {member.national_id}</p>}
                          {member.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{member.phone}</p>}
                          {member.is_linked && (
                            <Badge variant="secondary" className="text-xs">
                              <Link2 className="w-3 h-3 mr-1" />
                              {language === 'fr' ? 'Compte lié' : 'Linked'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              {language === 'fr' ? 'Mode de paiement' : 'Payment Method'}
            </DialogTitle>
            {selectedFee && <DialogDescription className="text-lg font-bold text-primary">{selectedFee.amount?.toFixed(2)} {selectedFee.currency}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <Button variant="outline" className="w-full justify-start gap-4 h-auto py-4" onClick={() => { setShowPaymentModal(false); handlePayFee(selectedFee?.fee_id, 'card'); }}>
              <div className="w-12 h-12 rounded bg-blue-100 flex items-center justify-center"><CreditCard className="w-6 h-6 text-blue-600" /></div>
              <div className="text-left"><p className="font-medium">{language === 'fr' ? 'Carte de crédit/débit' : 'Credit/Debit Card'}</p><p className="text-sm text-muted-foreground">Visa, Mastercard</p></div>
            </Button>
            <Button variant="outline" className="w-full justify-start gap-4 h-auto py-4" onClick={() => { setShowPaymentModal(false); handlePayFee(selectedFee?.fee_id, 'mobile_money'); }}>
              <div className="w-12 h-12 rounded bg-green-100 flex items-center justify-center"><Smartphone className="w-6 h-6 text-green-600" /></div>
              <div className="text-left"><p className="font-medium">Mobile Money</p><p className="text-sm text-muted-foreground">M-Pesa, Airtel, Orange</p></div>
            </Button>
            <Button variant="outline" className="w-full justify-start gap-4 h-auto py-4" onClick={() => { setShowPaymentModal(false); toast.info(language === 'fr' ? 'Virement: BIAC 0001234567890' : 'Transfer: BIAC 0001234567890'); }}>
              <div className="w-12 h-12 rounded bg-purple-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-purple-600" /></div>
              <div className="text-left"><p className="font-medium">{language === 'fr' ? 'Virement bancaire' : 'Bank Transfer'}</p></div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{language === 'fr' ? 'Aperçu du document' : 'Document Preview'}</span>
              <Button variant="outline" size="sm" onClick={() => { const btn = document.querySelector('[data-print-trigger]'); if (btn) btn.click(); }}>
                <Printer className="w-4 h-4 mr-2" />{language === 'fr' ? 'Imprimer' : 'Print'}
              </Button>
            </DialogTitle>
          </DialogHeader>
          {selectedFee && (
            <PrintableDocument
              ref={printRef}
              documentType={selectedFee.notification_type ? 'SUMMONS' : 'PERCEPTION_NOTE'}
              documentId={selectedFee.fee_id || selectedFee.notification_id}
              data={{
                ...selectedFee,
                citizen_name: profile?.user?.name,
                national_id: profile?.user?.national_id
              }}
              language={language}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Complaint Modal */}
      <Dialog open={showComplaintModal} onOpenChange={setShowComplaintModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Déposer une plainte' : 'File a Complaint'}</DialogTitle>
            <DialogDescription>{language === 'fr' ? 'Des frais de dépôt seront générés automatiquement' : 'Filing fees will be generated automatically'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de plainte' : 'Complaint Type'}</Label>
              <Select value={complaint.complaint_type} onValueChange={(v) => setComplaint({...complaint, complaint_type: v})}>
                <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRIME_REPORT">{language === 'fr' ? 'Signalement de crime' : 'Crime Report'}</SelectItem>
                  <SelectItem value="CIVIL_COMPLAINT">{language === 'fr' ? 'Plainte civile' : 'Civil Complaint'}</SelectItem>
                  <SelectItem value="ADMINISTRATIVE">{language === 'fr' ? 'Plainte administrative' : 'Administrative'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
              <Input value={complaint.title} onChange={(e) => setComplaint({...complaint, title: e.target.value})} placeholder={language === 'fr' ? 'Objet de la plainte' : 'Subject of complaint'} />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description détaillée' : 'Detailed Description'}</Label>
              <Textarea value={complaint.description} onChange={(e) => setComplaint({...complaint, description: e.target.value})} rows={4} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Date de l\'incident' : 'Incident Date'}</Label>
                <Input type="date" value={complaint.incident_date} onChange={(e) => setComplaint({...complaint, incident_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Lieu' : 'Location'}</Label>
                <Input value={complaint.incident_location} onChange={(e) => setComplaint({...complaint, incident_location: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Informations sur l\'accusé (optionnel)' : 'Accused Info (optional)'}</Label>
              <Textarea value={complaint.accused_info} onChange={(e) => setComplaint({...complaint, accused_info: e.target.value})} rows={2} />
            </div>
            
            {/* File Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {language === 'fr' ? 'Pièces jointes (photos, vidéos, audio, documents)' : 'Attachments (photos, videos, audio, documents)'}
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input 
                  type="file" 
                  id="complaint-files"
                  multiple 
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleComplaintFileSelect}
                />
                <label htmlFor="complaint-files" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-2">
                      <Image className="w-6 h-6 text-blue-500" />
                      <Video className="w-6 h-6 text-purple-500" />
                      <Mic className="w-6 h-6 text-green-500" />
                      <File className="w-6 h-6 text-gray-500" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr' 
                        ? 'Cliquez pour ajouter des fichiers (max 50MB par fichier)' 
                        : 'Click to add files (max 50MB per file)'}
                    </p>
                  </div>
                </label>
              </div>
              
              {/* Selected Files List */}
              {complaintFiles.length > 0 && (
                <div className="space-y-2 mt-2">
                  {complaintFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file)}
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        <span className="text-xs text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeComplaintFile(index)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <Button onClick={handleFileComplaint} className="w-full" disabled={!complaint.complaint_type || !complaint.title}>
              {language === 'fr' ? 'Déposer la plainte' : 'File Complaint'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Envoyer un message' : 'Send Message'}</DialogTitle>
            <DialogDescription>{language === 'fr' ? 'Communiquez avec le juge de votre dossier' : 'Communicate with your case judge'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Dossier concerné' : 'Related Case'}</Label>
              <Select value={newMessage.case_id} onValueChange={(v) => setNewMessage({...newMessage, case_id: v})}>
                <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner un dossier' : 'Select a case'} /></SelectTrigger>
                <SelectContent>
                  {profile?.cases?.map((c) => (
                    <SelectItem key={c.case_id} value={c.case_id}>{c.case_number} - {c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Sujet' : 'Subject'}</Label>
              <Input value={newMessage.subject} onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Message' : 'Message'}</Label>
              <Textarea value={newMessage.content} onChange={(e) => setNewMessage({...newMessage, content: e.target.value})} rows={5} />
            </div>
            <Button onClick={handleSendMessage} className="w-full gap-2" disabled={!newMessage.case_id || !newMessage.content}>
              <Send className="w-4 h-4" />
              {language === 'fr' ? 'Envoyer' : 'Send'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lawyer Modal */}
      <Dialog open={showLawyerModal} onOpenChange={setShowLawyerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Ajouter un avocat' : 'Add Lawyer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
              <Input value={newLawyer.name} onChange={(e) => setNewLawyer({...newLawyer, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Numéro du barreau' : 'Bar Number'}</Label>
              <Input value={newLawyer.bar_number} onChange={(e) => setNewLawyer({...newLawyer, bar_number: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                <Input value={newLawyer.phone} onChange={(e) => setNewLawyer({...newLawyer, phone: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newLawyer.email} onChange={(e) => setNewLawyer({...newLawyer, email: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
              <Input value={newLawyer.address} onChange={(e) => setNewLawyer({...newLawyer, address: e.target.value})} />
            </div>
            <Button onClick={handleAddLawyer} className="w-full" disabled={!newLawyer.name}>
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Family Modal */}
      <Dialog open={showFamilyModal} onOpenChange={setShowFamilyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Ajouter un membre de famille' : 'Add Family Member'}</DialogTitle>
            <DialogDescription>{language === 'fr' ? 'Si le membre a un ID national enregistré, son compte sera automatiquement lié' : 'If the member has a registered national ID, their account will be automatically linked'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Relation' : 'Relationship'}</Label>
              <Select value={newFamilyMember.relationship} onValueChange={(v) => setNewFamilyMember({...newFamilyMember, relationship: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FATHER">{language === 'fr' ? 'Père' : 'Father'}</SelectItem>
                  <SelectItem value="MOTHER">{language === 'fr' ? 'Mère' : 'Mother'}</SelectItem>
                  <SelectItem value="SPOUSE">{language === 'fr' ? 'Conjoint(e)' : 'Spouse'}</SelectItem>
                  <SelectItem value="CHILD">{language === 'fr' ? 'Enfant' : 'Child'}</SelectItem>
                  <SelectItem value="SIBLING">{language === 'fr' ? 'Frère/Sœur' : 'Sibling'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
              <Input value={newFamilyMember.name} onChange={(e) => setNewFamilyMember({...newFamilyMember, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Date de naissance' : 'Date of Birth'}</Label>
                <Input type="date" value={newFamilyMember.date_of_birth} onChange={(e) => setNewFamilyMember({...newFamilyMember, date_of_birth: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'ID National' : 'National ID'}</Label>
                <Input value={newFamilyMember.national_id} onChange={(e) => setNewFamilyMember({...newFamilyMember, national_id: e.target.value})} placeholder={language === 'fr' ? 'Pour liaison auto' : 'For auto-linking'} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                <Input value={newFamilyMember.phone} onChange={(e) => setNewFamilyMember({...newFamilyMember, phone: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={newFamilyMember.email} onChange={(e) => setNewFamilyMember({...newFamilyMember, email: e.target.value})} />
              </div>
            </div>
            <Button onClick={handleAddFamily} className="w-full" disabled={!newFamilyMember.relationship || !newFamilyMember.name}>
              {language === 'fr' ? 'Ajouter' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CitizenPortalPage;
